/**
 * First-touch / last-touch capture — the one first-party script the website
 * and the app both run, so a sign-up on either carries the same
 * attribution shape.
 *
 * First touch is captured once, on a visitor's first arrival, and never
 * overwritten by a later visit: `utm_source/medium/campaign/content/term`,
 * `document.referrer`, the landing path, a referral or affiliate code from
 * the URL (`?ref=<code>` / `/r/<code>` / `/a/<code>`), and a timestamp — kept
 * in a first-party cookie for 30 days. Last touch is read fresh on every
 * call (the sign-up visit's own touch) and is never stored in a cookie of
 * its own.
 *
 * Nothing here is third-party: one cookie, no pixel, no beacon to anyone but
 * this app's own API at sign-up. Pseudonymous until then — see this
 * package's README-level note in GROWTH.md's G5 — and it goes with the
 * account on deletion, because it only ever becomes part of a user's
 * profile, never a separate record.
 */

const COOKIE_NAME = "spark_first_touch";
const COOKIE_MAX_AGE_DAYS = 30;

/**
 * What one visit captured. Mirrors `TouchSchema` (packages/core/src/types/
 * user.ts) field for field — kept as a separate type here rather than
 * importing core, which pulls in AWS SDKs this browser bundle must never
 * see.
 */
export interface Touch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  referrer?: string;
  landingPath?: string;
  referralCode?: string;
  affiliateCode?: string;
  capturedAt: string;
}

export interface SignupAttribution {
  firstTouch?: Touch;
  lastTouch?: Touch;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return undefined;
  const value = match.slice(name.length + 1);
  try {
    return decodeURIComponent(value);
  } catch (_err) {
    // A malformed percent-encoding is not this script's to fix — read as
    // "no cookie", the same as one genuinely absent.
    return undefined;
  }
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (typeof document === "undefined") return;
  const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
}

/** `/r/<code>` or `/a/<code>` from a path — capture only, nothing else reads these yet. */
function codeFromPath(pathname: string): {
  referralCode?: string;
  affiliateCode?: string;
} {
  const referral = /^\/r\/([^/?#]+)/.exec(pathname);
  if (referral?.[1]) return { referralCode: referral[1] };
  const affiliate = /^\/a\/([^/?#]+)/.exec(pathname);
  if (affiliate?.[1]) return { affiliateCode: affiliate[1] };
  return {};
}

/** Reads the current page's UTM params, referrer, landing path and ref/affiliate code into a Touch. */
function captureTouch(): Touch {
  const params = new URLSearchParams(window.location.search);
  const { referralCode, affiliateCode } = codeFromPath(
    window.location.pathname
  );

  const touch: Touch = {
    capturedAt: new Date().toISOString(),
  };
  const utmSource = params.get("utm_source") ?? undefined;
  const utmMedium = params.get("utm_medium") ?? undefined;
  const utmCampaign = params.get("utm_campaign") ?? undefined;
  const utmContent = params.get("utm_content") ?? undefined;
  const utmTerm = params.get("utm_term") ?? undefined;
  // `?ref=<code>` per GROWTH.md's naming, same capture-only treatment as the
  // path form above.
  const refParam = params.get("ref") ?? undefined;

  if (utmSource) touch.utmSource = utmSource;
  if (utmMedium) touch.utmMedium = utmMedium;
  if (utmCampaign) touch.utmCampaign = utmCampaign;
  if (utmContent) touch.utmContent = utmContent;
  if (utmTerm) touch.utmTerm = utmTerm;
  if (document.referrer) touch.referrer = document.referrer;
  if (window.location.pathname) touch.landingPath = window.location.pathname;
  if (referralCode || refParam) touch.referralCode = referralCode ?? refParam;
  if (affiliateCode) touch.affiliateCode = affiliateCode;

  return touch;
}

/**
 * Call once, as early as possible, on every page load of the website and
 * the app (see SiteApp.tsx and the frontend's app entry). Writes the
 * first-touch cookie only if it is not already set — a later visit, with
 * different UTM params or none at all, must never overwrite the visitor's
 * true first touch.
 */
export function captureFirstTouch(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  if (readCookie(COOKIE_NAME)) return;

  const touch = captureTouch();
  try {
    writeCookie(COOKIE_NAME, JSON.stringify(touch), COOKIE_MAX_AGE_DAYS);
  } catch (_err) {
    // A full or disabled cookie jar is a visitor-side condition this script
    // cannot fix. Attribution for this visitor is simply unrecorded —
    // nothing downstream must treat that as a zero or a crash.
  }
}

/** The stored first touch, or undefined if the cookie is absent/unparsable. */
export function getFirstTouch(): Touch | undefined {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && parsed.capturedAt
      ? (parsed as Touch)
      : undefined;
  } catch (_err) {
    return undefined;
  }
}

/**
 * The attribution to send with a sign-up: the stored first touch (if any)
 * plus a fresh last touch captured from the CURRENT page — the sign-up
 * visit itself, never stored in its own cookie. Call this at the moment of
 * submitting registration (email/password or the Google sign-in redirect),
 * not earlier, so `lastTouch` reflects the page the user actually signed up
 * from.
 */
export function getSignupAttribution(): SignupAttribution {
  const firstTouch = getFirstTouch();
  const lastTouch = captureTouch();
  return {
    ...(firstTouch ? { firstTouch } : {}),
    lastTouch,
  };
}

/** Base64url-encodes attribution for a URL query param — see GoogleAuthButton.tsx. */
export function encodeAttributionForQuery(
  attribution: SignupAttribution
): string {
  return typeof window === "undefined" || typeof btoa === "undefined"
    ? ""
    : window
        .btoa(JSON.stringify(attribution))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}
